import moment from 'moment';
import { PONTOS_DA_ROTA } from './dados';
import { ATRASO_SIMULADO_MS, comoLista, contarComVolumePor, contarPor, empresaPorId, falhar, normalizar, ofertas, paginar, viagens } from './estado';
import { resolverDoTransportador } from './transportadora';

const textoDaOferta = (oferta) => normalizar([
    oferta.numero, oferta.embarcadora?.emp_nome_fantasia, oferta.produto_acondicionamento?.produto_nome, oferta.numero_documento, oferta.numero_pedido,
].join(' '));

const listarOfertas = (parametros) => {
    const termo = normalizar(parametros.termo);
    const status = comoLista(parametros.status);

    const doFiltroBase = ofertas
        .filter((oferta) => !termo || textoDaOferta(oferta).includes(termo))
        .filter((oferta) => !parametros.data_inicio || moment(oferta.data_fim_operacao).isSameOrAfter(parametros.data_inicio, 'day'))
        .filter((oferta) => !parametros.data_fim || moment(oferta.data_inicio_operacao).isSameOrBefore(parametros.data_fim, 'day'));

    const filtradas = doFiltroBase.filter((oferta) => status.length === 0 || status.includes(oferta.status));
    const { data, pagination } = paginar([...filtradas].sort((a, b) => b.id - a.id), parametros);

    return { data, meta: { pagination, counts: contarPor(doFiltroBase, 'status') } };
};

const listarViagens = (parametros) => {
    const termo = normalizar(parametros.termo);
    const status = comoLista(parametros.status);

    const doFiltroBase = viagens
        .filter((viagem) => !parametros.oferta_id || Number(viagem.oferta_id) === Number(parametros.oferta_id))
        .filter((viagem) => !termo || normalizar([viagem.numero, viagem.motorista.nome, viagem.cavalo.placa, viagem.carroceria.placa].join(' ')).includes(termo));

    const filtradas = doFiltroBase.filter((viagem) => status.length === 0 || status.includes(viagem.status));
    const { data, pagination } = paginar(filtradas, parametros);

    return { data, meta: { pagination, counts: contarComVolumePor(doFiltroBase, 'status') } };
};

const buscarOferta = (id) => ofertas.find((oferta) => Number(oferta.id) === Number(id));

const criarOferta = (carga) => {
    const id = Math.max(...ofertas.map((oferta) => oferta.id)) + 1;
    const nova = {
        id,
        numero: String(1000 + id),
        status: 'em_analise',
        embarcadora: empresaPorId(carga.embarcadora_id, 'Embarcadora'),
        armazem_origem: empresaPorId(carga.armazem_origem_id, 'Armazém'),
        armazem_destino: empresaPorId(carga.armazem_destino_id, 'Armazém'),
        patio_origem: carga.patio_origem_id ? empresaPorId(carga.patio_origem_id, 'Pátio') : null,
        patio_destino: carga.patio_destino_id ? empresaPorId(carga.patio_destino_id, 'Pátio') : null,
        remetente: empresaPorId(carga.remetente_id, 'Remetente'),
        destinatario: empresaPorId(carga.destinatario_id, 'Destinatário'),
        produto_acondicionamento: { id: carga.produto_acondicionamento_id, nome: 'Acondicionamento selecionado', produto_nome: 'Produto selecionado' },
        tipos_caminhao: carga.tipos_caminhao || [],
        quantidades_eixos: carga.quantidades_eixos || [],
        tipos_carroceria: carga.tipos_carroceria || [],
        numero_pedido: carga.numero_pedido,
        numero_di: carga.numero_di,
        tipo_documento: carga.tipo_documento,
        numero_documento: carga.numero_documento,
        nome_navio: carga.nome_navio,
        observacao: carga.observacao,
        volume_total: carga.volume_total,
        data_inicio_operacao: carga.data_inicio_operacao,
        data_fim_operacao: carga.data_fim_operacao,
        cadencia: carga.cadencia || {},
        orientacoes_motorista: carga.orientacoes_motorista,
        valores_por_eixo: carga.valores_por_eixo || [],
        divulgacao: carga.divulgacao,
        movimentacao_liberada: false,
        volumes: { agendado: 0, carregado: 0, descarregado: 0, avaria_kg: 0 },
        operacao: {},
    };
    const valores = (nova.valores_por_eixo || []).map((item) => Number(item.valor_por_tonelada || 0));
    nova.valor_medio_com_icms_por_tonelada = valores.length ? valores.reduce((soma, valor) => soma + valor, 0) / valores.length : 0;
    nova.valor_total_estimado = Math.round(nova.valor_medio_com_icms_por_tonelada * Number(nova.volume_total || 0));
    ofertas.push(nova);

    return nova;
};


const PEDAGIO_POR_EIXO = 21.4;

const calcularRota = (carga) => ({
    distancia_km: 2140,
    pracas_pedagio: 23,
    pedagios_por_eixo: (carga.quantidades_eixos || []).map((quantidade) => ({
        quantidade_eixos: quantidade,
        valor: Math.round(quantidade * PEDAGIO_POR_EIXO * 23 * 100) / 100,
    })),
    pontos: PONTOS_DA_ROTA,
});

const ALIQUOTA_DO_ICMS = 12;
const VIAGENS_POR_MIL_TONELADAS = 34;

const arredondar = (valor) => Math.round(valor * 100) / 100;

const validarValores = (carga) => {
    const itens = carga.valores_por_eixo || [];
    const media = itens.length ? itens.reduce((soma, item) => soma + Number(item.valor_por_tonelada || 0), 0) / itens.length : 0;
    const pedagios = carga.rota?.pedagios_por_eixo || [];
    const pedagioMedio = pedagios.length ? pedagios.reduce((soma, item) => soma + Number(item.valor || 0), 0) / pedagios.length : 0;
    const volume = Number(carga.volume_total || 0);
    const viagensEstimadas = Math.ceil((volume / 1000) * VIAGENS_POR_MIL_TONELADAS);

    return {
        aliquota_icms: ALIQUOTA_DO_ICMS,
        referencias_antt: (carga.quantidades_eixos || []).map((quantidade) => ({ quantidade_eixos: quantidade, piso_por_tonelada: 30 + quantidade * 6 })),
        valor_medio_com_icms_por_tonelada: arredondar(media),
        icms_medio_por_tonelada: arredondar(media * ALIQUOTA_DO_ICMS / 100),
        valor_total_estimado: arredondar(media * volume + pedagioMedio * viagensEstimadas),
        pedagio_total_estimado: arredondar(pedagioMedio * viagensEstimadas),
    };
};

const calcularAlcance = () => ({
    regioes: [
        { cidade: 'Sorriso', uf: 'MT', bairro: 'Centro', veiculos: 42 },
        { cidade: 'Sorriso', uf: 'MT', bairro: 'Jardim Primavera', veiculos: 18 },
        { cidade: 'Sinop', uf: 'MT', bairro: 'Setor Industrial', veiculos: 31 },
        { cidade: 'Lucas do Rio Verde', uf: 'MT', bairro: 'Distrito Industrial', veiculos: 27 },
        { cidade: 'Nova Mutum', uf: 'MT', bairro: 'Centro', veiculos: 14 },
    ],
});


const FINALIZADO = 'finalizado';

const locaisDaOferta = (oferta) => [
    ['ARMAZEM', oferta?.armazem_origem, 'ARMAZEM_ORIGEM'],
    ['PATIO', oferta?.patio_origem, 'PATIO_PROGRAMACAO'],
    ['PATIO', oferta?.patio_destino, 'PATIO_PROGRAMACAO'],
    ['ARMAZEM', oferta?.armazem_destino, 'ARMAZEM_DESTINO'],
].filter(([, empresa]) => empresa).map(([tipo, empresa, papel]) => ({
    tipo,
    id: empresa.id,
    nome: empresa.emp_nome_fantasia,
    latitude: Number(empresa.emp_latitude),
    longitude: Number(empresa.emp_longitude),
    localizacao_exata: true,
    papeis: [papel],
}));

const indicadoresDe = (ativas) => ({
    em_operacao: ativas.length,
    a_caminho: ativas.filter((viagem) => viagem.status === 'a_caminho_carregamento').length,
    carregando: ativas.filter((viagem) => ['patio_origem', 'aguardando_carregamento'].includes(viagem.status)).length,
    em_transito: ativas.filter((viagem) => viagem.status === 'em_transito').length,
    atrasados: ativas.filter((viagem) => viagem.situacao === 'atrasado').length,
    sem_sinal: ativas.filter((viagem) => viagem.situacao === 'sem_sinal').length,
    ocorrencias: ativas.filter((viagem) => viagem.ocorrencia_ativa).length,
});

const monitorar = (parametros) => {
    const oferta = ofertas.find((item) => Number(item.id) === Number(parametros.oferta_id));
    const ativas = viagens.filter((viagem) => Number(viagem.oferta_id) === Number(parametros.oferta_id) && viagem.status !== FINALIZADO);
    const { data, pagination } = paginar(ativas, parametros);

    return { data, meta: { pagination, indicadores: indicadoresDe(ativas), locais: locaisDaOferta(oferta) } };
};

const trilhaDe = (viagem) => {
    if (!viagem?.posicao) return [];
    const origem = viagem.oferta?.armazem_origem;
    const passos = 12;
    return Array.from({ length: passos + 1 }, (_, indice) => ({
        latitude: Number(origem.emp_latitude) + ((viagem.posicao.latitude - Number(origem.emp_latitude)) * indice) / passos,
        longitude: Number(origem.emp_longitude) + ((viagem.posicao.longitude - Number(origem.emp_longitude)) * indice) / passos,
        capturado_em: moment().subtract((passos - indice) * 20, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
    }));
};

const buscarViagem = (id) => viagens.find((viagem) => Number(viagem.id) === Number(id));

const resolverDoEmbarcador = (metodo, caminho, parametros, corpo) => {
    if (metodo === 'get' && caminho === '') return { status: 200, data: listarOfertas(parametros) };
    if (metodo === 'get' && caminho === '/viagens') return { status: 200, data: listarViagens(parametros) };
    if (metodo === 'post' && caminho === '') return { status: 201, data: { data: criarOferta(corpo) } };
    if (metodo === 'post' && caminho === '/rota') return { status: 200, data: { data: calcularRota(corpo) } };
    if (metodo === 'post' && caminho === '/validar-valores') return { status: 200, data: { data: validarValores(corpo) } };
    if (metodo === 'post' && caminho === '/alcance') return { status: 200, data: { data: calcularAlcance(corpo) } };
    if (metodo === 'get' && caminho === '/monitoramento') return { status: 200, data: monitorar(parametros) };

    if (metodo === 'get' && /^\/viagens\/\d+\/trilha$/.test(caminho)) {
        return { status: 200, data: { data: trilhaDe(buscarViagem(caminho.split('/')[2])) } };
    }

    if (metodo === 'get' && /^\/viagens\/\d+$/.test(caminho)) {
        const viagem = buscarViagem(caminho.split('/')[2]);
        return viagem ? { status: 200, data: { data: viagem } } : falhar(404, 'Viagem não encontrada.');
    }

    if (metodo === 'get' && /^\/\d+$/.test(caminho)) {
        const oferta = buscarOferta(caminho.slice(1));
        return oferta ? { status: 200, data: { data: oferta } } : falhar(404, 'Oferta não encontrada.');
    }

    return falhar(404, 'Rota do painel de ofertas ainda não simulada.');
};

const BASES = [
    { prefixo: '/embarcadora/painel-oferta', resolver: resolverDoEmbarcador },
    { prefixo: '/transportadora/painel-oferta', resolver: resolverDoTransportador },
];

const localizarBase = (url) => {
    const caminho = String(url).split('?')[0];
    const base = BASES.find((item) => caminho.includes(item.prefixo));
    if (!base) return null;
    return { resolver: base.resolver, caminho: caminho.slice(caminho.indexOf(base.prefixo) + base.prefixo.length).replace(/\/$/, '') };
};

const lerCorpo = (dados) => {
    if (!dados) return {};
    try {
        return typeof dados === 'string' ? JSON.parse(dados) : dados;
    } catch (erro) {
        return {};
    }
};

export const responderAoPainelDeOfertas = (configuracao) => {
    const destino = localizarBase(configuracao.url);
    if (destino === null) return null;

    const resposta = destino.resolver(String(configuracao.method || 'get').toLowerCase(), destino.caminho, configuracao.params || {}, lerCorpo(configuracao.data));

    return new Promise((resolve, reject) => setTimeout(() => {
        const completa = { ...resposta, statusText: resposta.status < 400 ? 'OK' : 'Not Found', headers: {}, config: configuracao, request: {} };
        if (resposta.status >= 400) {
            const erro = new Error(resposta.data.message);
            erro.isAxiosError = true;
            erro.response = completa;
            erro.config = configuracao;
            reject(erro);
            return;
        }
        resolve(completa);
    }, ATRASO_SIMULADO_MS));
};
